(() => {
  const MARQUEE_TAG = 'hk-announcement-marquee';

  if (!customElements.get(MARQUEE_TAG)) {
    class HKAnnouncementMarquee extends HTMLElement {
      constructor() {
        super();
        this.resizeObserver = null;
        this.originalMarkup = '';
        this.frame = 0;
      }

      connectedCallback() {
        const firstGroup = this.querySelector('[data-marquee-group]');
        if (!firstGroup) return;

        this.originalMarkup = firstGroup.innerHTML;
        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        this.resizeObserver.observe(this);

        if (document.fonts?.ready) {
          document.fonts.ready.then(() => this.scheduleLayout());
        } else {
          this.scheduleLayout();
        }
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.frame);
      }

      scheduleLayout() {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.layout());
      }

      layout() {
        const track = this.querySelector('[data-marquee-track]');
        const groups = Array.from(track?.children || []);
        const firstGroup = groups[0];
        const secondGroup = groups[1];
        if (!track || !firstGroup || !secondGroup || !this.originalMarkup) return;

        track.style.animation = 'none';
        firstGroup.innerHTML = this.originalMarkup;

        // A group must be wider than the viewport or even a perfectly duplicated
        // track can reveal empty space on very wide screens. Repeat the six-item
        // set inside each half until the invariant is satisfied.
        const targetWidth = this.clientWidth + 80;
        let safety = 0;
        while (firstGroup.scrollWidth < targetWidth && safety < 8) {
          firstGroup.insertAdjacentHTML('beforeend', this.originalMarkup);
          safety += 1;
        }

        secondGroup.innerHTML = firstGroup.innerHTML;
        const distance = firstGroup.getBoundingClientRect().width;
        const pixelsPerSecond = Math.max(Number(this.dataset.speed) || 55, 1);
        this.style.setProperty('--hk-marquee-duration', `${distance / pixelsPerSecond}s`);

        // Force a style flush before restoring the animation so Theme Editor
        // resizing always restarts from a valid, measured pair of groups.
        void track.offsetWidth;
        track.style.animation = '';
      }
    }

    customElements.define(MARQUEE_TAG, HKAnnouncementMarquee);
  }

  const getFocusable = (container) =>
    Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.getClientRects().length > 0);

  const initializeHeader = (header) => {
    if (!(header instanceof HTMLElement) || header.dataset.hkHeaderReady === 'true') return;
    header.dataset.hkHeaderReady = 'true';
    document.querySelectorAll('body > .hk-inline-nav__dropdown.is-portaled').forEach((dropdown) => dropdown.remove());

    const browser = header.querySelector('[data-hk-products-browser]');
    const openButton = header.querySelector('[data-hk-products-open]');
    const closeButtons = header.querySelectorAll('[data-hk-products-close]');
    const browserSurface = browser?.querySelector('.hk-products-browser__surface');
    let previousFocus = null;

    const inlineMenuDetails = Array.from(header.querySelectorAll('.hk-inline-nav__details'));
    const languagePicker = header.querySelector('[data-hk-language-picker]');
    const languageForm = languagePicker?.querySelector('.hk-language-picker__form');
    const languageOptions = Array.from(languageForm?.querySelectorAll('[data-hk-language-option]') || []);
    const restoreInlineMenu = (details) => {
      const dropdown = document.body.querySelector(`.hk-inline-nav__dropdown[data-hk-menu-owner="${details.dataset.hkMenuOwner}"]`);
      if (!dropdown) return;
      details.appendChild(dropdown);
      dropdown.classList.remove('is-portaled');
      dropdown.removeAttribute('data-hk-menu-owner');
      dropdown.style.removeProperty('top');
      dropdown.style.removeProperty('left');
      dropdown.style.removeProperty('visibility');
    };

    const closeInlineMenus = () => {
      inlineMenuDetails.forEach((details) => {
        if (details.open) details.open = false;
        restoreInlineMenu(details);
      });
    };

    inlineMenuDetails.forEach((details, index) => {
      const ownerId = `${header.id || 'hk-header'}-${index}`;
      details.dataset.hkMenuOwner = ownerId;
      details.addEventListener('toggle', () => {
        if (!details.open) {
          restoreInlineMenu(details);
          return;
        }

        if (languagePicker?.open) languagePicker.open = false;

        inlineMenuDetails.forEach((other) => {
          if (other !== details && other.open) other.open = false;
        });

        const summary = details.querySelector('summary');
        const dropdown = details.querySelector('.hk-inline-nav__dropdown');
        if (!summary || !dropdown) return;
        const headerStyles = getComputedStyle(header);
        ['--hk-header-bg', '--hk-header-text', '--hk-header-accent', '--hk-header-border'].forEach((property) => {
          dropdown.style.setProperty(property, headerStyles.getPropertyValue(property));
        });
        dropdown.dataset.hkMenuOwner = ownerId;
        dropdown.classList.add('is-portaled');
        dropdown.style.visibility = 'hidden';
        document.body.appendChild(dropdown);

        const triggerRect = summary.getBoundingClientRect();
        const dropdownWidth = dropdown.getBoundingClientRect().width;
        const left = Math.min(Math.max(triggerRect.left, 16), window.innerWidth - dropdownWidth - 16);
        dropdown.style.left = `${left}px`;
        dropdown.style.top = `${Math.max(triggerRect.bottom + 8, header.getBoundingClientRect().bottom + 8)}px`;
        dropdown.style.visibility = '';
      });
    });

    document.addEventListener('pointerdown', (event) => {
      const openDetails = inlineMenuDetails.find((details) => details.open);
      if (!openDetails) return;
      const dropdown = document.body.querySelector(`.hk-inline-nav__dropdown[data-hk-menu-owner="${openDetails.dataset.hkMenuOwner}"]`);
      if (openDetails.contains(event.target) || dropdown?.contains(event.target)) return;
      closeInlineMenus();
    });

    window.addEventListener('resize', closeInlineMenus);

    const closeBrowser = () => {
      if (!browser?.classList.contains('is-open')) return;
      browser.classList.remove('is-open');
      browser.setAttribute('aria-hidden', 'true');
      openButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('hk-products-browser-is-open');
      document.body.style.removeProperty('overflow');
      previousFocus?.focus?.();
    };

    const openBrowser = () => {
      if (!browser || !browserSurface) return;
      if (browser.classList.contains('is-open')) {
        closeBrowser();
        return;
      }
      closeInlineMenus();
      if (languagePicker?.open) languagePicker.open = false;
      previousFocus = document.activeElement;
      browser.classList.add('is-open');
      browser.setAttribute('aria-hidden', 'false');
      openButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('hk-products-browser-is-open');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        browser.querySelector('[role="tab"][aria-selected="true"]')?.focus() || browserSurface.focus();
        const activePanel = browser.querySelector('[data-hk-browser-product-panel]:not([hidden])');
        if (activePanel) updateVariantControls(activePanel);
      });
    };

    languagePicker?.addEventListener('toggle', () => {
      if (!languagePicker.open) return;
      closeInlineMenus();
      closeBrowser();
    });

    languagePicker?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !languagePicker.open) return;
      event.preventDefault();
      languagePicker.open = false;
      languagePicker.querySelector('summary')?.focus();
    });

    document.addEventListener('pointerdown', (event) => {
      if (!languagePicker?.open || languagePicker.contains(event.target)) return;
      languagePicker.open = false;
    });

    languageOptions.forEach((option) => {
      option.addEventListener('click', (event) => {
        if (!languageForm || option.disabled) return;
        const languageCode = option.dataset.languageCode;
        if (!languageCode) return;
        if (option.getAttribute('aria-current') === 'true') {
          event.preventDefault();
          languagePicker.open = false;
          return;
        }
        languageOptions.forEach((item) => {
          if (item === option) return;
          item.disabled = true;
          item.setAttribute('aria-disabled', 'true');
        });
        option.setAttribute('aria-busy', 'true');
      });
    });

    openButton?.addEventListener('click', openBrowser);
    closeButtons.forEach((button) => button.addEventListener('click', closeBrowser));
    browser?.addEventListener('shopify:cart:lines-update', () => {
      const addedFromBrowser = browser.classList.contains('is-open');
      if (!addedFromBrowser) return;

      closeBrowser();
      const cartDrawerTrigger = header.querySelector('[data-testid="cart-drawer-trigger"]');
      if (!cartDrawerTrigger) return;

      window.setTimeout(() => {
        if (cartDrawerTrigger.getAttribute('aria-expanded') !== 'true') cartDrawerTrigger.click();
      }, 120);
    });

    browser?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeBrowser();
        return;
      }

      if (event.key !== 'Tab' || !browserSurface) return;
      const focusable = getFocusable(browserSurface);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && (document.activeElement === first || document.activeElement === browserSurface)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const productTabs = Array.from(header.querySelectorAll('[data-hk-browser-product-tab]'));
    const productPanels = Array.from(header.querySelectorAll('[data-hk-browser-product-panel]'));

    const updateVariantControls = (panel) => {
      const rail = panel?.querySelector('[data-hk-browser-variant-rail]');
      if (!rail) return;
      const maximumScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
      const previousButton = panel.querySelector('[data-hk-browser-variant-scroll="previous"]');
      const nextButton = panel.querySelector('[data-hk-browser-variant-scroll="next"]');
      const hasOverflow = maximumScroll > 2;

      if (previousButton) previousButton.disabled = !hasOverflow || rail.scrollLeft <= 2;
      if (nextButton) nextButton.disabled = !hasOverflow || rail.scrollLeft >= maximumScroll - 2;
    };

    productPanels.forEach((panel) => {
      const rail = panel.querySelector('[data-hk-browser-variant-rail]');
      if (!rail) return;

      panel.querySelectorAll('[data-hk-browser-variant-scroll]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.hkBrowserVariantScroll === 'previous' ? -1 : 1;
          const card = rail.querySelector('.hk-browser-variant-card');
          const gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 0;
          const step = card ? card.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
          rail.scrollBy({ left: direction * step, behavior: 'smooth' });
        });
      });

      rail.addEventListener('scroll', () => updateVariantControls(panel), { passive: true });
      const railObserver = new ResizeObserver(() => updateVariantControls(panel));
      railObserver.observe(rail);
    });

    const activateProduct = (tab, { focus = false } = {}) => {
      if (!(tab instanceof HTMLElement)) return;
      const productId = tab.dataset.hkBrowserProductTab;

      productTabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });

      productPanels.forEach((item) => {
        const active = item.dataset.hkBrowserProductPanel === productId;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        if (active) {
          item.querySelector('[data-hk-browser-variant-rail]')?.scrollTo({ left: 0, behavior: 'auto' });
          requestAnimationFrame(() => updateVariantControls(item));
        }
      });

      tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      if (focus) tab.focus();
    };

    productTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateProduct(tab));
      tab.addEventListener('keydown', (event) => {
        let targetIndex;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') targetIndex = (index + 1) % productTabs.length;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') targetIndex = (index - 1 + productTabs.length) % productTabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = productTabs.length - 1;
        if (targetIndex === undefined) return;
        event.preventDefault();
        activateProduct(productTabs[targetIndex], { focus: true });
      });
    });

    requestAnimationFrame(() => {
      const activePanel = productPanels.find((panel) => !panel.hidden);
      if (activePanel) updateVariantControls(activePanel);
    });

    const mobileSearch = header.querySelector('[data-hk-mobile-search]');
    const mobileSearchToggle = header.querySelector('[data-hk-search-toggle]');

    mobileSearchToggle?.addEventListener('click', () => {
      if (!mobileSearch) return;
      const isOpening = mobileSearch.hidden;
      if (isOpening && languagePicker?.open) languagePicker.open = false;
      mobileSearch.hidden = !isOpening;
      mobileSearchToggle.setAttribute('aria-expanded', String(isOpening));
      mobileSearchToggle.setAttribute('aria-label', isOpening ? 'Close search' : 'Open search');
      if (isOpening) requestAnimationFrame(() => mobileSearch.querySelector('input')?.focus());
    });

    let config = { placeholders: [], interval: 3000, voiceAutoSubmit: true, language: 'en' };
    try {
      config = { ...config, ...JSON.parse(header.querySelector('[data-hk-search-config]')?.textContent || '{}') };
    } catch (error) {
      // Keep the safe defaults if a merchant-entered value cannot be parsed.
    }

    const searchInputs = Array.from(header.querySelectorAll('[data-hk-search-input]'));
    const placeholders = Array.from(
      new Set((config.placeholders || []).filter((value) => typeof value === 'string' && value.trim()))
    );
    let placeholderIndex = 0;

    if (placeholders.length) {
      searchInputs.forEach((input) => {
        if (!input.value) input.placeholder = placeholders[0];
      });
    }

    if (placeholders.length > 1) {
      window.setInterval(() => {
        if (document.hidden) return;
        placeholderIndex = (placeholderIndex + 1) % placeholders.length;
        searchInputs.forEach((input) => {
          if (!input.value) input.placeholder = placeholders[placeholderIndex];
        });
      }, Math.max(Number(config.interval) || 3000, 1500));
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const voiceStatus = header.querySelector('[data-hk-voice-status]');

    if (SpeechRecognition) {
      header.querySelectorAll('[data-hk-voice]').forEach((button) => {
        button.hidden = false;
        let recognition = null;

        button.addEventListener('click', () => {
          const form = button.closest('form');
          const input = form?.querySelector('[data-hk-search-input]');
          if (!form || !input) return;

          if (recognition) {
            recognition.stop();
            return;
          }

          recognition = new SpeechRecognition();
          recognition.lang = config.language || document.documentElement.lang || 'en';
          recognition.interimResults = true;
          recognition.continuous = false;

          recognition.onstart = () => {
            button.classList.add('is-listening');
            button.setAttribute('aria-label', 'Stop voice search');
            if (voiceStatus) voiceStatus.textContent = 'Listening. Say a product name.';
          };

          recognition.onresult = (event) => {
            const result = Array.from(event.results)
              .map((item) => item[0]?.transcript || '')
              .join('')
              .trim();
            input.value = result;

            const finalResult = event.results[event.results.length - 1]?.isFinal;
            if (finalResult && result) {
              if (voiceStatus) voiceStatus.textContent = `Searching for ${result}`;
              if (config.voiceAutoSubmit) window.setTimeout(() => form.requestSubmit(), 250);
            }
          };

          recognition.onerror = (event) => {
            if (voiceStatus) {
              voiceStatus.textContent = event.error === 'not-allowed'
                ? 'Microphone permission was not granted.'
                : 'Voice search could not hear you. Please try again.';
            }
          };

          recognition.onend = () => {
            button.classList.remove('is-listening');
            button.setAttribute('aria-label', 'Search by voice');
            recognition = null;
          };

          recognition.start();
        });
      });
    }

    const updateHeaderMeasurements = () => {
      const group = document.querySelector('#header-group');
      if (!group) return;
      header.style.setProperty('--hk-current-header-box-height', `${header.offsetHeight}px`);
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const browserTop = browser?.getBoundingClientRect().top ?? header.getBoundingClientRect().bottom;
      const availableBrowserHeight = Math.max(viewportHeight - browserTop - 18, 280);
      header.style.setProperty('--hk-products-available-height', `${availableBrowserHeight}px`);
      document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
      const groupHeight = Array.from(group.children).reduce(
        (height, element) => height + (element instanceof HTMLElement ? element.offsetHeight : 0),
        0
      );
      document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    };

    const headerObserver = new ResizeObserver(updateHeaderMeasurements);
    headerObserver.observe(header);
    window.addEventListener('resize', updateHeaderMeasurements, { passive: true });
    window.visualViewport?.addEventListener('resize', updateHeaderMeasurements, { passive: true });
    updateHeaderMeasurements();

    // A small direction threshold prevents trackpad jitter and mobile bounce.
    const stickySection = header.closest('.hk-premium-header-section');
    if (stickySection && header.dataset.stickyEnabled === 'true') {
      const scrollEvents = new AbortController();
      let scrollFrame = 0;
      const scrollSurface = document.querySelector('.page-wrapper');
      const usesScrollSurface = () => scrollSurface && scrollSurface.scrollHeight > scrollSurface.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(scrollSurface).overflowY);
      const readScrollY = () => usesScrollSurface() ? scrollSurface.scrollTop : window.scrollY;
      let previousY = Math.max(0, readScrollY());
      let directionStart = previousY;
      let direction = 0;
      const revealHeader = () => stickySection.classList.remove('is-scroll-hidden');
      const updateScrollDirection = () => {
        scrollFrame = 0;
        if (!header.isConnected) {
          scrollEvents.abort();
          return;
        }
        const maxY = Math.max(0, usesScrollSurface() ? scrollSurface.scrollHeight - scrollSurface.clientHeight : document.documentElement.scrollHeight - window.innerHeight);
        const y = Math.min(Math.max(0, readScrollY()), maxY);
        const delta = y - previousY;
        const active = document.activeElement;
        const editingHeader = header.contains(active) &&
          (active.matches('input, textarea, select') || active.matches(':focus-visible'));
        const menuOpen = browser?.classList.contains('is-open') || languagePicker?.open ||
          inlineMenuDetails.some((details) => details.open) ||
          (mobileSearch && !mobileSearch.hidden) ||
          header.querySelector('[data-testid="cart-drawer-trigger"][aria-expanded="true"]');
        if (y <= 24 || editingHeader || menuOpen) {
          revealHeader();
          directionStart = y;
          direction = 0;
        } else if (delta !== 0) {
          const nextDirection = Math.sign(delta);
          if (nextDirection !== direction) {
            directionStart = previousY;
            direction = nextDirection;
          }
          const travelled = Math.abs(y - directionStart);
          if (direction < 0 && travelled >= 6) revealHeader();
          if (direction > 0 && travelled >= 10 && y > header.offsetHeight) {
            stickySection.classList.add('is-scroll-hidden');
          }
        }
        previousY = y;
      };
      const queueScrollUpdate = () => {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScrollDirection);
      };
      window.addEventListener('scroll', queueScrollUpdate, { passive: true, signal: scrollEvents.signal });
      scrollSurface?.addEventListener('scroll', queueScrollUpdate, { passive: true, signal: scrollEvents.signal });
      window.addEventListener('resize', () => { previousY = readScrollY(); directionStart = previousY; direction = 0; revealHeader(); }, { signal: scrollEvents.signal });
      header.addEventListener('focusin', revealHeader, { signal: scrollEvents.signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(header)) return;
        cancelAnimationFrame(scrollFrame);
        scrollEvents.abort();
      }, { signal: scrollEvents.signal });
    }
  };

  const initializeAll = (root = document) => {
    if (root instanceof HTMLElement && root.matches('[data-hk-premium-header]')) initializeHeader(root);
    root.querySelectorAll?.('[data-hk-premium-header]').forEach(initializeHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  if (!window.hkPremiumHeaderEditorBound) {
    window.hkPremiumHeaderEditorBound = true;
    document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  }
})();
